import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_177ec162-a789-4cad-ab14-accead9ebd01/artifacts/53213un4_Logo%20Meraki.png";

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { label: { es: 'Inicio', en: 'Home' }, path: '/' },
    { label: { es: 'Nosotros', en: 'About' }, path: language === 'es' ? '/nosotros' : '/about' },
    { label: { es: 'Servicios', en: 'Services' }, path: language === 'es' ? '/servicios' : '/services' },
    { label: { es: 'Talento', en: 'Talent' }, path: language === 'es' ? '/talento' : '/talent' },
    { label: { es: 'Contacto', en: 'Contact' }, path: language === 'es' ? '/contacto' : '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, delay: 2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-black/95 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <motion.img 
              src={LOGO_URL} 
              alt="MERAKI" 
              className="h-10 w-auto"
              whileHover={{ scale: 1.05 }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative text-sm font-medium transition-colors ${
                  isActive(item.path) ? 'text-[#D91616]' : 'text-zinc-400 hover:text-white'
                }`}
              >
                {item.label[language]}
                {isActive(item.path) && (
                  <motion.div layoutId="activeNav" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#D91616]" />
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-6">
            <button onClick={toggleLanguage} className="flex items-center space-x-2 text-sm font-medium text-zinc-400 hover:text-white">
              <span className={language === 'es' ? 'text-white' : 'text-zinc-500'}>ES</span>
              <span className="text-zinc-600">/</span>
              <span className={language === 'en' ? 'text-white' : 'text-zinc-500'}>EN</span>
            </button>

            <Link
              to={language === 'es' ? '/contacto' : '/contact'}
              className="bg-[#D91616] hover:bg-[#ED1C24] text-white px-6 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all"
            >
              {language === 'es' ? 'Aplica Ahora' : 'Apply Now'}
            </Link>
          </div>

          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-white">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <nav className="max-w-7xl mx-auto px-6 py-6 space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block text-lg font-medium ${isActive(item.path) ? 'text-[#D91616]' : 'text-zinc-400'}`}
                >
                  {item.label[language]}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/10">
                <button onClick={toggleLanguage} className="text-sm text-zinc-400">
                  {language === 'es' ? 'English' : 'Español'}
                </button>
              </div>
              <Link
                to={language === 'es' ? '/contacto' : '/contact'}
                className="block w-full text-center bg-[#D91616] text-white px-6 py-3 font-semibold uppercase tracking-wider"
              >
                {language === 'es' ? 'Aplica Ahora' : 'Apply Now'}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
